import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthClient, HttpClient, enableConsoleLogs } from '@passport/api-client';
import { sessionConfig } from '@passport/config';
import { AUTH_STORE_STORAGE_KEY } from '@passport/constants';
import { decodeTenantFullIDFromJwt } from '@bizfirst/tenant-identity';
import { registerAuthSource } from '@passport/auth-session';

// Login/sign-in flow diagnostic logging throughout this store (login/verifyToken/etc. below) —
// gated behind enableConsoleLogs.loginProcess. DO NOT remove these console.log statements in
// future changes; toggle the flag instead. See debugFlags.ts in @passport/api-client.

export interface MfaChallenge {
  userId: number;
  challengeToken: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  /**
   * The authenticated user's real tenant ID, mapped from the profile response's
   * `tenantID` field (the `User` domain entity's `TenantID` column, already carried on
   * the JWT's `tenantId` claim too — see `AuthenticationService.GenerateAccessTokenAsync`
   * in the backend). Consuming apps should read this instead of hardcoding a tenant ID
   * or reading a build-time env var — see form-studio's/flow-studio's/hil's own
   * DevelopmentHistoryLog.md entries for the incident this replaces.
   *
   * `login()`/`handleGoogleCallback()`/`verifyMfa()` now populate this from the JWT (via
   * `decodeTenantFullIDFromJwt`, alongside `tenantSlug`/`environment` below) rather than
   * leaving it undefined — a pre-existing gap found and fixed alongside adding the two new
   * fields; see `DevelopmentHistoryLog.md`.
   */
  tenantID?: number;
  /**
   * The authenticated user's tenant slug and deployment environment, decoded from the JWT's
   * `tenantSlug`/`environment` claims (`decodeTenantFullIDFromJwt`, `@bizfirst/tenant-identity`)
   * — see `BizFirstClaimTypes.cs` (backend) for the wire claim names this reads. Combined with
   * `tenantID` above, these three values form a `TenantFullID` — the globally-unique tenant
   * identity a bare `tenantID` alone cannot express (dev/staging/production share the same
   * numbering space). Additive: `undefined` on a token issued before these claims existed, or
   * when a caller has no token to decode yet — never treat a missing value as "unknown"/default,
   * only as "not yet known."
   */
  tenantSlug?: string;
  environment?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastActivity: number | null;
  sessionWarningVisible: boolean;
  mfaChallenge: MfaChallenge | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (tenantId?: number) => Promise<void>;
  handleGoogleCallback: (code: string, state: string) => Promise<void>;
  verifyMfa: (userId: number, code: string) => Promise<void>;
  clearMfaChallenge: () => void;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPasswordComplete: (token: string, newPassword: string) => Promise<void>;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  setExpiresAt: (expiresAt: number | null) => void;
  clearError: () => void;
  verifyToken: () => Promise<boolean>;
  updateActivity: () => void;
  checkSessionTimeout: () => void;
  dismissSessionWarning: () => void;
}

// Every distinct message string AuthenticationService.AuthenticateAsync can return for a
// failed login (backend source of truth — see BizFirst.IAM.Login.Service/Services/
// AuthenticationService.cs), mapped to a clearer, more actionable message for the login form.
// Matched by exact text since the backend's error `code` is "UNAUTHORIZED" for every one of
// these — there's no more granular signal to key off of.
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  'Invalid credentials':
    "We couldn't sign you in with that email and password. Please double-check both and try again.",
  'Account is locked. Please try again later.':
    'Your account has been temporarily locked after several unsuccessful sign-in attempts. Please wait 30 minutes and try again, or contact your administrator if you need access sooner.',
  'Account is inactive':
    'Your account is currently inactive. Please contact your administrator to have it reactivated.',
  'An error occurred during authentication':
    'Something went wrong on our end while signing you in. Please try again in a moment.',
};

/** Falls back to the backend's raw message for anything not in the map above, so an
 *  unrecognized future failure case still surfaces something instead of going silent. */
function describeLoginError(rawMessage: string): string {
  return LOGIN_ERROR_MESSAGES[rawMessage] ?? rawMessage;
}

/**
 * Reads the `role` claim straight out of the JWT's own payload rather than trusting a login
 * response's separate `user.roles` field — the backend's login/SSO/MFA endpoints have been
 * observed to leave that field empty even for accounts the token itself grants Admin/
 * TenantSuperAdmin/PlatformSuperAdmin (confirmed via a real token: the JWT's `role` claim had
 * all three, while `loginData.user.roles` was `[]`, so every gated app relying on the latter
 * denied access to an account that actually had it). The token is what every backend
 * authorization check already trusts, so it's the correct source of truth here too — no
 * dependency on the login response shape ever being fixed on the backend side.
 */
function decodeJwtRoles(token: string | undefined | null): string[] {
  if (!token) return [];
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return [];
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    const role = payload.role ?? payload.roles;
    if (Array.isArray(role)) return role;
    if (typeof role === 'string') return [role];
    return [];
  } catch {
    return [];
  }
}

// Initialize clients — use origin so /api/* paths route through the Vite proxy in dev.
// Exported so other stores in this package (roleStore, userStore) can build their own
// API clients (RoleClient, UserClient, ...) against this same token-synced instance
// instead of reaching into an app's own API layer, which would create a circular
// package dependency (an app importing @passport/store while @passport/store imports
// back into that app).
export const httpClient = new HttpClient(import.meta.env.VITE_API_BASE_URL || window.location.origin);
const authClient = new AuthClient(httpClient);

// Track if logout is in progress to prevent loops
let isLoggingOut = false;

// Set up token refresh callback for automatic 401 retry
httpClient.setRefreshCallback(async (refreshToken: string) => {
  try {
    // Only refresh if token is actually expired or close to expiry
    const state = useAuthStore.getState();
    const now = Date.now();
    const bufferMs = 30 * 1000; // 30 second buffer

    console.log('[AuthStore] Refresh callback triggered', {
      expiresAt: state.expiresAt,
      now,
      timeRemaining: state.expiresAt ? state.expiresAt - now : 'N/A',
      bufferMs,
      expiresAtType: typeof state.expiresAt,
    });

    if (state.expiresAt && state.expiresAt > now + bufferMs) {
      // Token is still valid, don't refresh
      console.log('[AuthStore] Token still valid, skipping refresh');
      throw new Error('Token still valid');
    }

    console.log('[AuthStore] Calling refresh-token API...');
    const response = await authClient.refreshToken({ refreshToken });
    const refreshData = response.data;

    const newAccessToken = refreshData.accessToken || refreshData.token;
    const newRefreshToken = refreshData.refreshToken;
    const expiresAtStr = refreshData.accessTokenExpiresAt || refreshData.expiresAt;
    const newExpiresAt = expiresAtStr ? new Date(expiresAtStr).getTime() : null;

    console.log('[AuthStore] Refresh API response', {
      hasNewAccessToken: !!newAccessToken,
      hasNewRefreshToken: !!newRefreshToken,
      newExpiresAt,
      newExpiresAtType: typeof newExpiresAt,
    });

    if (!newAccessToken || !newRefreshToken) {
      throw new Error('Invalid token refresh response');
    }

    // Update store with new tokens
    useAuthStore.getState().setToken(newAccessToken);
    useAuthStore.getState().setRefreshToken(newRefreshToken);
    useAuthStore.getState().setExpiresAt(newExpiresAt);
    useAuthStore.getState().updateActivity();

    console.log('[AuthStore] Token refreshed successfully', {
      expiresAt: newExpiresAt,
      expiresAtDate: newExpiresAt != null ? new Date(newExpiresAt) : null,
    });

    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    };
  } catch (error) {
    // Force logout on refresh failure (but prevent loops)
    if (!isLoggingOut) {
      isLoggingOut = true;
      try {
        useAuthStore.getState().logout();
      } finally {
        isLoggingOut = false;
      }
    }
    throw error;
  }
});

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        refreshToken: null,
        expiresAt: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        lastActivity: null,
        sessionWarningVisible: false,
        mfaChallenge: null,
        login: async (email: string, password: string) => {
          if (enableConsoleLogs.loginProcess) {
            console.log('[authStore.login] Form submitted', {
              email,
              apiBaseUrl: import.meta.env.VITE_API_BASE_URL || window.location.origin,
            });
          }
          set({ isLoading: true, error: null });
          try {
            const response = await authClient.login({ email, password });
            // httpClient now unwraps the GoWebResponse envelope automatically
            const loginData = (response as any).data ?? response;

            // MFA challenge — backend returns { MfaRequired: true, ChallengeToken: "...", UserId: N }
            // Check both camelCase and PascalCase for robustness
            if ((loginData as any).mfaRequired || (loginData as any).MfaRequired) {
              set({
                isLoading: false,
                mfaChallenge: {
                  userId: (loginData as any).userId || (loginData as any).UserId,
                  challengeToken: (loginData as any).challengeToken || (loginData as any).ChallengeToken,
                },
                error: null,
              });
              return;
            }

            // Handle both old and new field names from API response
            const accessToken = loginData.accessToken || loginData.token;
            const userId = loginData.user?.userID !== undefined
              ? String(loginData.user.userID)
              : (loginData.user?.id || '');
            const userName = loginData.user?.firstName && loginData.user?.lastName
              ? `${loginData.user.firstName} ${loginData.user.lastName}`
              : (loginData.user?.name || loginData.user?.email || '');

            // Handle expiresAt from either accessTokenExpiresAt or expiresAt field
            const expiresAtStr = loginData.accessTokenExpiresAt || loginData.expiresAt;
            const expiresAt = expiresAtStr ? new Date(expiresAtStr).getTime() : null;

            console.log('[AuthStore] Login successful', {
              userId,
              email: loginData.user.email,
              hasAccessToken: !!accessToken,
              hasRefreshToken: !!loginData.refreshToken,
              expiresAt,
              expiresAtDate: expiresAt ? new Date(expiresAt) : null,
              expiresAtType: typeof expiresAt,
            });

            // Decoded once and spread onto `user` below — see `decodeTenantFullIDFromJwt`'s doc
            // comment for why the JWT (not the login response's `user` DTO) is this store's
            // uniform source for tenantID/tenantSlug/environment. Also fixes a pre-existing gap:
            // this object literal previously omitted `tenantID` entirely (see
            // DevelopmentHistoryLog.md), leaving a direct-login user's `user.tenantID` undefined.
            const tenantFullID = decodeTenantFullIDFromJwt(accessToken);

            set({
              user: {
                id: userId,
                email: loginData.user.email,
                name: userName,
                roles: loginData.user.roles?.length ? loginData.user.roles : decodeJwtRoles(accessToken),
                permissions: [],
                ...(tenantFullID
                  ? {
                      tenantID: tenantFullID.tenantID,
                      tenantSlug: tenantFullID.tenantSlug,
                      environment: tenantFullID.environment,
                    }
                  : {}),
              },
              token: accessToken,
              refreshToken: loginData.refreshToken,
              expiresAt,
              isAuthenticated: true,
              isLoading: false,
              lastActivity: Date.now(),
              sessionWarningVisible: false,
            });

            httpClient.setToken(accessToken);
            httpClient.setRefreshToken(loginData.refreshToken);

            console.log('[AuthStore] Tokens set in httpClient and Zustand store');
            if (enableConsoleLogs.loginProcess) {
              console.log('[authStore.login] isAuthenticated set to TRUE — LoginPage\'s success effect will now redirect', {
                userId,
                isAuthenticated: true,
              });
            }
          } catch (error) {
            const rawMessage = error instanceof Error ? error.message : (error as any)?.message || 'Login failed';
            // The backend's login endpoint always returns code "UNAUTHORIZED" for every failure
            // case — wrong password, unknown email, locked account, disabled account, and an
            // unexpected server error are all indistinguishable by code, only by this raw message
            // string (see AuthenticationService.AuthenticateAsync). Map the ones we know about to
            // a clearer, more actionable message; anything unrecognized falls back to the
            // backend's raw text as-is, so a future new failure case still surfaces something
            // rather than going silent.
            const errorMessage = describeLoginError(rawMessage);
            if (enableConsoleLogs.loginProcess) {
              console.log('[authStore.login] Login FAILED — staying on sign-in form', {
                rawMessage,
                displayedError: errorMessage,
              });
            }
            // Clear tokens on login failure to prevent 401 loops
            set({
              error: errorMessage,
              isLoading: false,
              user: null,
              token: null,
              refreshToken: null,
              isAuthenticated: false,
              mfaChallenge: null,
            });
            httpClient.clearToken();
            httpClient.setRefreshToken(null as any);
          }
        },
        loginWithGoogle: async (tenantId = 1) => {
          set({ isLoading: true, error: null });
          try {
            const response = await authClient.googleLogin({ tenantId });
            const data = (response as any).data ?? response;
            // SsoAuthUrlWebResponse uses authUrl; old GoogleLoginInitiateResponse used authorizationUrl
            const authUrl = data.authUrl || data.AuthUrl || data.authorizationUrl || data.AuthorizationUrl;
            if (!authUrl) throw new Error('No authorization URL returned from server');
            // Navigate to Google consent screen — page will reload to login URL with ?code=&state=
            window.location.href = authUrl;
          } catch (error) {
            const msg = error instanceof Error ? error.message : (error as any)?.message || 'Google sign-in failed';
            set({ error: msg, isLoading: false });
          }
        },
        handleGoogleCallback: async (code: string, state: string) => {
          set({ isLoading: true, error: null });
          try {
            const response = await authClient.googleCallback({ code, state });
            const data = (response as any).data ?? response;
            const accessToken = data.accessToken || data.AccessToken;
            // SsoAuthWebResponse nests user fields under .user; GoogleLoginResult had flat fields
            const user = data.user ?? data;
            const userId = user.userId ?? user.UserId ?? data.userId ?? data.UserId;
            const email = user.email || user.Email || data.email || data.Email || '';
            const displayName = user.displayName || user.DisplayName || data.displayName || data.DisplayName;
            const refreshToken = data.refreshToken || data.RefreshToken;
            const expiresAtStr = data.accessTokenExpiresAt || data.expiresAt;
            const expiresAt = expiresAtStr ? new Date(expiresAtStr).getTime() : null;
            // See the identical comment in `login()` above — same fix, same reasoning.
            const tenantFullID = decodeTenantFullIDFromJwt(accessToken);

            set({
              user: {
                id: String(userId),
                email,
                name: displayName || email,
                roles: decodeJwtRoles(accessToken),
                permissions: [],
                ...(tenantFullID
                  ? {
                      tenantID: tenantFullID.tenantID,
                      tenantSlug: tenantFullID.tenantSlug,
                      environment: tenantFullID.environment,
                    }
                  : {}),
              },
              token: accessToken,
              refreshToken,
              expiresAt,
              isAuthenticated: true,
              isLoading: false,
              lastActivity: Date.now(),
              sessionWarningVisible: false,
            });
            httpClient.setToken(accessToken);
            httpClient.setRefreshToken(refreshToken);
          } catch (error) {
            const msg = error instanceof Error ? error.message : (error as any)?.message || 'Google sign-in failed';
            set({ error: msg, isLoading: false });
          }
        },
        verifyMfa: async (userId: number, code: string) => {
          set({ isLoading: true, error: null });
          try {
            const response = await authClient.verifyMfa({ userId, code });
            const mfaData = (response as any).data ?? response;
            const accessToken = mfaData.accessToken || mfaData.token;
            const userName = mfaData.user?.firstName && mfaData.user?.lastName
              ? `${mfaData.user.firstName} ${mfaData.user.lastName}`
              : (mfaData.user?.name || mfaData.user?.email || '');
            const expiresAtStr = mfaData.accessTokenExpiresAt || mfaData.expiresAt;
            const expiresAt = expiresAtStr ? new Date(expiresAtStr).getTime() : null;
            // See the identical comment in `login()` above — same fix, same reasoning.
            const tenantFullID = decodeTenantFullIDFromJwt(accessToken);

            set({
              user: {
                id: String(mfaData.user?.userID || mfaData.user?.id || ''),
                email: mfaData.user.email,
                name: userName,
                roles: mfaData.user.roles?.length ? mfaData.user.roles : decodeJwtRoles(accessToken),
                permissions: [],
                ...(tenantFullID
                  ? {
                      tenantID: tenantFullID.tenantID,
                      tenantSlug: tenantFullID.tenantSlug,
                      environment: tenantFullID.environment,
                    }
                  : {}),
              },
              token: accessToken,
              refreshToken: mfaData.refreshToken,
              expiresAt,
              isAuthenticated: true,
              isLoading: false,
              mfaChallenge: null,
              lastActivity: Date.now(),
              sessionWarningVisible: false,
            });
            httpClient.setToken(accessToken);
            httpClient.setRefreshToken(mfaData.refreshToken);
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : (error as any)?.message || 'MFA verification failed';
            set({ error: errorMessage, isLoading: false });
          }
        },
        clearMfaChallenge: () => {
          set({ mfaChallenge: null, error: null });
        },
        logout: async () => {
          set({ isLoading: true });
          try {
            const { refreshToken, token } = get();
            // Only call logout API if we have a valid token
            if (refreshToken || token) {
              try {
                await authClient.logout(refreshToken ?? undefined);
              } catch (apiError) {
                // Log but don't throw — logout locally even if API fails
                console.warn('Logout API failed, clearing local session', apiError);
              }
            }
            httpClient.clearToken();
            httpClient.setRefreshToken(null as any);

            // Fix #9: Clear CSRF token on logout (token rotation on sensitive operation)
            try {
              const { useAuthzStore } = await import('./authzStore');
              useAuthzStore.getState().clearAuthz();
            } catch (e) {
              const isExpectedError =
                e instanceof TypeError ||
                (e instanceof Error &&
                  (e.message.includes('not defined') ||
                    e.message.includes('not a function') ||
                    e.message.includes('Cannot read properties') ||
                    e.message.includes('useAuthzStore')));
              if (!isExpectedError) {
                console.error('[AuthStore] Unexpected error clearing authz on logout:', e);
              }
            }

            set({
              user: null,
              token: null,
              refreshToken: null,
              expiresAt: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              lastActivity: null,
              sessionWarningVisible: false,
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Logout failed';
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },
        refreshAccessToken: async () => {
          const state = get();
          if (!state.refreshToken) {
            set({ error: 'No refresh token available' });
            return;
          }

          set({ isLoading: true });
          try {
            const response = await authClient.refreshToken({
              refreshToken: state.refreshToken,
            });
            const refreshData = response.data;

            // Handle both old and new field names from API response
            const newAccessToken = refreshData.accessToken || refreshData.token;
            if (!newAccessToken) {
              throw new Error('Invalid token refresh response');
            }
            const expiresAtStr = refreshData.accessTokenExpiresAt || refreshData.expiresAt;
            const expiresAt = expiresAtStr ? new Date(expiresAtStr).getTime() : null;

            set({
              token: newAccessToken,
              refreshToken: refreshData.refreshToken,
              expiresAt,
              isLoading: false,
            });

            httpClient.setToken(newAccessToken);
            if (refreshData.refreshToken) {
              httpClient.setRefreshToken(refreshData.refreshToken);
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
            set({
              error: errorMessage,
              isLoading: false,
              isAuthenticated: false,
              user: null,
              token: null,
              refreshToken: null,
              expiresAt: null,
            });
            throw error;
          }
        },
        changePassword: async (currentPassword: string, newPassword: string) => {
          set({ isLoading: true, error: null });
          try {
            const state = get();
            if (!state.user?.id) {
              throw new Error('User not authenticated');
            }
            const userId = parseInt(state.user.id);
            if (isNaN(userId)) {
              throw new Error('Invalid user ID');
            }

            // Call change password API
            await authClient.changePassword(userId, currentPassword, newPassword);

            // Password changed successfully - now logout to invalidate old session
            try {
              await authClient.logout(state.refreshToken || undefined);
            } catch (logoutError) {
              console.warn('Logout after password change failed:', logoutError);
              // Continue anyway - we'll clear state locally
            }

            // Clear all auth state
            set({
              user: null,
              token: null,
              refreshToken: null,
              expiresAt: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              lastActivity: null,
              sessionWarningVisible: false,
            });

            httpClient.clearToken();
            httpClient.setRefreshToken(null as any);

            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Password change failed';
            set({ error: errorMessage, isLoading: false });
            return false;
          }
        },
        register: async (firstName: string, lastName: string, email: string, password: string) => {
          set({ isLoading: true, error: null });
          try {
            await authClient.register({ firstName, lastName, email, password });
            set({ isLoading: false, error: null });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Registration failed';
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },
        forgotPassword: async (email: string) => {
          set({ isLoading: true, error: null });
          try {
            await authClient.forgotPassword({ email });
            set({ isLoading: false, error: null });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Forgot password request failed';
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },
        resetPasswordComplete: async (token: string, newPassword: string) => {
          set({ isLoading: true, error: null });
          try {
            await authClient.resetPasswordComplete({ token, newPassword });
            set({ isLoading: false, error: null });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },
        setUser: (user: User) => {
          // Every app's cross-app token-handoff path (App.tsx: setToken(urlToken) then
          // getProfile().then(setUser)) calls this with whatever the IAM profile endpoint
          // returned — which, like the login endpoint (see decodeJwtRoles' doc comment above),
          // has been observed to omit roles even for an already-verified token that carries
          // them. Backfilling from the current token here fixes every consumer of setUser at
          // once rather than needing the same fallback repeated in each app's own App.tsx.
          const roles = user.roles?.length ? user.roles : decodeJwtRoles(get().token);
          // The IAM profile response carries `tenantID` (already mapped onto `user.tenantID`
          // above the caller) but never `tenantSlug`/`environment` — those two exist only as JWT
          // claims (see `decodeTenantFullIDFromJwt`'s doc comment). Backfill them from the
          // current token the same way `roles` is backfilled just above; `tenantID` itself is
          // left exactly as the caller passed it in — this is additive-only, never overriding an
          // already-resolved profile value.
          const tenantFullID = decodeTenantFullIDFromJwt(get().token);
          set({
            user: {
              ...user,
              roles,
              tenantSlug: user.tenantSlug ?? tenantFullID?.tenantSlug,
              environment: user.environment ?? tenantFullID?.environment,
            },
            isAuthenticated: true,
          });
        },
        setToken: (token: string) => {
          set({ token });
          httpClient.setToken(token);
        },
        setRefreshToken: (refreshToken: string) => {
          set({ refreshToken });
          httpClient.setRefreshToken(refreshToken);
        },
        setExpiresAt: (expiresAt: number | null) => {
          set({ expiresAt });
        },
        clearError: () => {
          set({ error: null });
        },
        verifyToken: async () => {
          if (enableConsoleLogs.loginProcess) {
            console.log('[authStore.verifyToken] Verifying session', { hasToken: !!get().token });
          }
          try {
            const response = await authClient.verifyToken();
            const valid = response.data?.valid ?? false;
            if (enableConsoleLogs.loginProcess) {
              console.log('[authStore.verifyToken] Result', { valid });
            }
            if (!valid) {
              httpClient.clearToken();
              httpClient.setRefreshToken(null as any);
              set({ user: null, token: null, refreshToken: null, expiresAt: null, isAuthenticated: false });
            }
            return valid;
          } catch (error) {
            // Only logout on 401 (token invalid) — not on network errors
            const isAuthError = (error as any)?.statusCode === 401;
            if (enableConsoleLogs.loginProcess) {
              console.log('[authStore.verifyToken] Threw — treating as', {
                isAuthError,
                treatedAsValid: !isAuthError,
                error,
              });
            }
            if (isAuthError) {
              httpClient.clearToken();
              httpClient.setRefreshToken(null as any);
              set({ user: null, token: null, refreshToken: null, expiresAt: null, isAuthenticated: false });
            }
            // On network errors, keep authenticated state; on auth errors, return false
            return !isAuthError;
          }
        },
        updateActivity: () => {
          set({ lastActivity: Date.now(), sessionWarningVisible: false });
        },
        checkSessionTimeout: () => {
          const state = get();
          if (!state.isAuthenticated || !state.lastActivity) {
            return;
          }

          const now = Date.now();
          const inactiveTime = now - state.lastActivity;
          const timeoutMs = sessionConfig.timeoutMs;
          const warningMs = sessionConfig.warningMs;

          // Show warning if approaching timeout (within warning window)
          const timeUntilTimeout = timeoutMs - inactiveTime;
          if (timeUntilTimeout <= warningMs && timeUntilTimeout > 0) {
            if (!state.sessionWarningVisible) {
              set({ sessionWarningVisible: true });
            }
          }

          // Auto-logout if timeout exceeded
          if (inactiveTime >= timeoutMs) {
            // Call logout API asynchronously (fire-and-forget)
            authClient.logout(state.refreshToken || undefined).catch(() => {
              // Silently ignore API errors — we're logging out locally anyway
            });

            set({
              isAuthenticated: false,
              user: null,
              token: null,
              refreshToken: null,
              expiresAt: null,
              sessionWarningVisible: false,
              error: 'Session expired due to inactivity',
              lastActivity: null,
            });
            httpClient.clearToken();
          }
        },
        dismissSessionWarning: () => {
          set({ sessionWarningVisible: false });
        },
      }),
      {
        name: AUTH_STORE_STORAGE_KEY,
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          expiresAt: state.expiresAt,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => (state) => {
          // Restore the access token and refresh token to the http client after page reload
          if (state?.token) {
            httpClient.setToken(state.token);
          }
          if (state?.refreshToken) {
            httpClient.setRefreshToken(state.refreshToken);
          }
        },
      }
    )
  )
);

// Registers this store as a possible source of the current session for
// @passport/auth-session's fetchAuthToken(), the single accessor every non-hook call site
// (outside a React render) should read the current token/session through instead of
// re-implementing `useAuthStore.getState().token` itself. This is the store the real, deployed
// login app routes both login flows into (see @passport/pages/LoginPage.tsx's own comment on
// why) — see passport-shared-auth-accessor-design.md for the full rationale.
registerAuthSource('@passport/store', () => {
  const state = useAuthStore.getState();
  if (!state.isAuthenticated || !state.token) return null;
  return {
    token: state.token,
    refreshToken: state.refreshToken,
    expiresAt: state.expiresAt,
    user: state.user ?? { id: '', email: '', name: '', roles: [], permissions: [] },
  };
});
